export function formatCurrency(
  amount: number,
  options: {
    showSymbol?: boolean;
    locale?: string;
  } = {}
): string {
  const { showSymbol = true, locale = "ko-KR" } = options;

  const formatted = new Intl.NumberFormat(locale, {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  return showSymbol ? `${formatted}원` : formatted;
}

export function formatDate(
  date: Date | string | null | undefined,
  options: {
    includeTime?: boolean;
    locale?: string;
    format?: "short" | "long" | "full";
  } = {}
): string {
  if (!date) return "-";

  const {
    includeTime = false,
    locale = "ko-KR",
    format = "long",
  } = options;

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return "-";

  const baseDateFormatOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: format === "short" ? "numeric" : "long",
    day: "numeric",
  };

  const dateFormatOptions: Intl.DateTimeFormatOptions = includeTime
    ? {
        ...baseDateFormatOptions,
        hour: "2-digit",
        minute: "2-digit",
        hour12: locale === "ko-KR",
      }
    : baseDateFormatOptions;

  return new Intl.DateTimeFormat(locale, dateFormatOptions).format(dateObj);
}

export function formatRelativeTime(
  date: Date | string | null | undefined,
  options: {
    locale?: string;
  } = {}
): string {
  if (!date) return "-";

  const { locale = "ko-KR" } = options;
  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return "-";

  const now = new Date();
  const diffInSeconds = Math.floor(
    (now.getTime() - dateObj.getTime()) / 1000
  );

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (diffInSeconds < 60) {
    return rtf.format(-diffInSeconds, "second");
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return rtf.format(-diffInMinutes, "minute");
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return rtf.format(-diffInHours, "hour");
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return rtf.format(-diffInDays, "day");
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return rtf.format(-diffInMonths, "month");
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return rtf.format(-diffInYears, "year");
}

export function formatNumber(
  value: number | null | undefined,
  options: {
    decimals?: number;
    locale?: string;
  } = {}
): string {
  if (value === null || value === undefined) return "-";

  const { decimals = 0, locale = "ko-KR" } = options;

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

export function formatPriceWithCycle(
  amount: number,
  cycle: "MONTHLY" | "YEARLY"
): string {
  const formatted = formatCurrency(amount);
  const cycleText = cycle === "MONTHLY" ? "/월" : "/년";
  return `${formatted}${cycleText}`;
}

export function formatNextPayment(
  dateString: string | undefined
): { text: string; isOverdue: boolean } {
  if (!dateString) return { text: "-", isOverdue: false };

  const date = new Date(dateString);
  const now = new Date();
  const isOverdue = date < now;

  if (isOverdue) return { text: `연체 (${formatDate(dateString)})`, isOverdue: true };

  return { text: formatDate(dateString), isOverdue: false };
}
